import React, { useState, useEffect } from 'react';
import { BookMarked, FileText, Users, BookOpen } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { surahAPI, mealAPI, reciterAPI } from '../../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Dashboard = () => {
  const [stats, setStats] = useState({
    surahs: 0,
    meals: 0,
    reciters: 0,
    verses: 0
  });
  const [loading, setLoading] = useState(true);
  const [siteTexts, setSiteTexts] = useState({});

  useEffect(() => {
    loadStats();
    loadSiteTexts();
  }, []);

  const loadSiteTexts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/site-texts`);
      const texts = await response.json();
      const textsMap = {};
      texts.forEach(text => {
        textsMap[text.key] = text.value;
      });
      setSiteTexts(textsMap);
    } catch (error) {
      console.error('Error loading site texts:', error);
    }
  };

  const getText = (key, defaultValue = '') => {
    return siteTexts[key] || defaultValue;
  };

  const loadStats = async () => {
    try {
      const [surahsRes, mealsRes, recitersRes] = await Promise.all([
        surahAPI.getAll(),
        mealAPI.getAll(),
        reciterAPI.getAll()
      ]);

      setStats({
        surahs: surahsRes.data.length,
        meals: mealsRes.data.length,
        reciters: recitersRes.data.length,
        verses: surahsRes.data.reduce((sum, s) => sum + s.verses, 0)
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Toplam Sure', value: stats.surahs, icon: BookMarked, color: 'bg-blue-500' },
    { title: 'Toplam Ayet', value: stats.verses, icon: FileText, color: 'bg-green-500' },
    { title: 'Toplam Meal', value: stats.meals, icon: FileText, color: 'bg-purple-500' },
    { title: 'Toplam Hafiz', value: stats.reciters, icon: Users, color: 'bg-orange-500' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[#8B7355]">{getText('reader.loading', 'Yükleniyor...')}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-[#5C4033] mb-2">{getText('admin.welcome', 'Hoşgeldiniz!')}</h2>
        <p className="text-[#8B7355]">{getText('admin.panel_desc', 'Kuran-ı Kerim yönetim panelinize hoşgeldiniz.')}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="bg-white border-[#E8DCC8] p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#8B7355] mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold text-[#5C4033]">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-4 rounded-lg`}>
                  <Icon className="h-8 w-8 text-white" />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card className="bg-white border-[#E8DCC8] p-6">
        <h3 className="text-xl font-bold text-[#5C4033] mb-4">Hizli Islemler</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <a href="/admin/surahs" className="p-4 border-2 border-[#E8DCC8] rounded-lg hover:border-[#D5C4A1] hover:bg-[#F9F6F1] transition-colors">
            <BookMarked className="h-8 w-8 text-[#8B7355] mb-2" />
            <p className="font-semibold text-[#5C4033]">Sure Yonetimi</p>
            <p className="text-sm text-[#8B7355]">Sure ekle veya duzenle</p>
          </a>
          <a href="/admin/meals" className="p-4 border-2 border-[#E8DCC8] rounded-lg hover:border-[#D5C4A1] hover:bg-[#F9F6F1] transition-colors">
            <FileText className="h-8 w-8 text-[#8B7355] mb-2" />
            <p className="font-semibold text-[#5C4033]">Meal Yonetimi</p>
            <p className="text-sm text-[#8B7355]">Meal ekle veya duzenle</p>
          </a>
          <a href="/admin/reciters" className="p-4 border-2 border-[#E8DCC8] rounded-lg hover:border-[#D5C4A1] hover:bg-[#F9F6F1] transition-colors">
            <Users className="h-8 w-8 text-[#8B7355] mb-2" />
            <p className="font-semibold text-[#5C4033]">Hafiz Yonetimi</p>
            <p className="text-sm text-[#8B7355]">Hafiz ekle veya duzenle</p>
          </a>
          <a href="/" className="p-4 border-2 border-[#E8DCC8] rounded-lg hover:border-[#D5C4A1] hover:bg-[#F9F6F1] transition-colors">
            <BookOpen className="h-8 w-8 text-[#8B7355] mb-2" />
            <p className="font-semibold text-[#5C4033]">Siteye Git</p>
            <p className="text-sm text-[#8B7355]">Ana siteyi goruntule</p>
          </a>
        </div>
      </Card>
    </div>
  );
};

export default Dashboard;